import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, Trash2, Image, Link2, X, User, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";

export default function Comments({ bugId }) {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [content, setContent] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bugs/${bugId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bugId]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: Max file size is 5MB`);
        continue;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setImages((prev) => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    }
    
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/bugs/${bugId}/comments`, {
        content: content.trim(),
        images,
        video_url: videoUrl || null
      }, { withCredentials: true });
      
      // Clear form first
      setContent("");
      setVideoUrl("");
      setImages([]);
      
      // Update comments list with new comment from response
      if (response.data && response.data.comment) {
        // API returns { status: "success", comment: {...} }
        setComments(prev => [...prev, response.data.comment]);
        toast.success("Comment added!");
      } else {
        // Fallback - refetch all comments
        await fetchComments();
        toast.success("Comment added!");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error(error.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    
    try {
      await axios.delete(`${API}/bugs/${bugId}/comments/${commentId}`, { withCredentials: true });
      toast.success("Comment deleted");
      fetchComments();
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error(error.response?.data?.detail || "Failed to delete comment");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="bg-card border-border/40 mt-6">
      <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
        <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          Comments ({comments.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 md:p-4">
        {/* Comments List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : comments.length > 0 ? (
          <div className="space-y-4 mb-6">
            {comments.map((comment) => (
              <div key={comment.id} className="bg-muted/20 rounded-sm p-4 border border-border/40">
                {/* Comment Header */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    {comment.user_picture ? (
                      <img 
                        src={comment.user_picture} 
                        alt={comment.user_name} 
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-body font-semibold text-foreground">{comment.user_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{formatDate(comment.created_at)}</p>
                    </div>
                  </div>
                  
                  {/* Delete button - only for comment owner or admin */}
                  {isAuthenticated && user && (user.user_id === comment.user_id || user.role === 'admin') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(comment.id)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {/* Comment Content */}
                <p className="text-sm text-foreground font-body whitespace-pre-wrap">{comment.content}</p>
                
                {/* Comment Images */}
                {comment.images && comment.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {comment.images.map((img, i) => (
                      <a key={`${comment.id}-img-${i}`} href={img} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={img} 
                          alt={`Attachment ${i + 1}`} 
                          className="w-20 h-20 object-cover rounded-sm border border-border/40 hover:border-primary/50 transition-colors"
                        />
                      </a>
                    ))}
                  </div>
                )}
                
                {/* Comment Video */}
                {comment.video_url && (
                  <a 
                    href={comment.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-mono mt-3"
                  >
                    <Link2 className="w-3 h-3" />
                    Video Link
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground font-mono text-center py-6">No comments yet</p>
        )}

        {/* Add Comment Form - Only for authenticated users with comment permission (tester+) */}
        {isAuthenticated && ["admin", "developer", "tester"].includes(user?.role) ? (
          <form onSubmit={handleSubmit} className="space-y-4 border-t border-border/40 pt-4">
            <div>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Add a comment... (Markdown supported)"
                className="min-h-[100px] bg-black/20 border-border/50 focus:border-primary font-body"
                data-testid="comment-input"
              />
            </div>
            
            {/* Video URL */}
            <div>
              <Input
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="Video URL (optional)"
                className="bg-black/20 border-border/50 focus:border-primary font-mono text-sm"
              />
            </div>
            
            {/* Image Upload */}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                className="hidden"
              />
              
              <div className="flex flex-wrap gap-2">
                {images.map((img, i) => (
                  <div key={`upload-${i}-${(img || '').slice(0, 32)}`} className="relative group">
                    <img
                      src={img}
                      alt={`Upload ${i + 1}`}
                      className="w-16 h-16 object-cover rounded-sm border border-border/40"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-16 h-16 border-2 border-dashed border-border/40 rounded-sm flex items-center justify-center text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
                >
                  <Image className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={submitting || !content.trim()}
              className="font-heading font-bold uppercase tracking-wider"
              data-testid="submit-comment-btn"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Post Comment
                </>
              )}
            </Button>
          </form>
        ) : isAuthenticated ? (
          <div className="border-t border-border/40 pt-4 text-center" data-testid="comment-no-permission">
            <p className="text-sm text-muted-foreground font-mono">
              Only QA staff can post internal comments
            </p>
          </div>
        ) : (
          <div className="border-t border-border/40 pt-4 text-center">
            <p className="text-sm text-muted-foreground font-mono">
              Sign in to leave a comment
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
